import React, { useState } from 'react'
import Modal from 'react-modal'

Modal.setAppElement('#root')

const ModalView = ({ open, close, email, analysis }) => {
  const [activeTab, setActiveTab] = useState('summary')

  // Remove UUID prefix from filename
  const cleanFilename = (filename) => {
    if (!filename) return ''
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i
    return filename.replace(uuidPattern, '')
  }

  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
    },
    content: {
      position: 'relative',
      inset: 'auto',
      border: 'none',
      background: 'white',
      borderRadius: '0.75rem',
      padding: 0,
      maxWidth: '900px',
      width: '100%',
      maxHeight: '85vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
  }

  const getThreatLevel = (analysis) => {
    if (!analysis || analysis.error) return null

    const summary = analysis.layman_summary || {}
    const isSafe = summary.is_safe || false
    const riskLevel = (summary.risk_level || '').toLowerCase()

    if (isSafe) {
      return { level: 'safe', label: 'Safe Email', icon: '✓', description: 'No threats detected' }
    } else if (riskLevel.includes('high') || riskLevel.includes('critical')) {
      return { level: 'danger', label: 'High Risk', icon: '!', description: 'Immediate action required' }
    } else if (riskLevel.includes('medium')) {
      return { level: 'warning', label: 'Medium Risk', icon: '⚠', description: 'Exercise caution' }
    }

    return { level: 'unknown', label: 'Unknown', icon: '?', description: 'Unable to determine' }
  }

  const threat = getThreatLevel(analysis)

  // Simple value rendering
  const renderValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400 text-sm">N/A</span>
    }

    if (typeof value === 'boolean') {
      return (
        <span className={value ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400 text-sm">None</span>
      }
      return (
        <ul className="list-disc list-inside space-y-1">
          {value.map((item, idx) => (
            <li key={idx} className="text-sm text-gray-700">{String(item)}</li>
          ))}
        </ul>
      )
    }

    if (typeof value === 'object') {
      return (
        <div className="space-y-1 text-sm">
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="flex justify-between">
              <span className="text-gray-600">{k.replace(/_/g, ' ')}:</span>
              <span className="text-gray-900 font-medium">{String(v)}</span>
            </div>
          ))}
        </div>
      )
    }

    return <span className="text-sm text-gray-900">{String(value)}</span>
  }

  const renderSummary = () => {
    if (!analysis?.layman_summary) return null

    const summary = analysis.layman_summary

    return (
      <div className="space-y-4">
        {/* Compact Threat Assessment */}
        {threat && (
          <div className={`rounded-lg border p-4 ${
            threat.level === 'safe' ? 'bg-green-50 border-green-300' :
            threat.level === 'danger' ? 'bg-red-50 border-red-300' :
            threat.level === 'warning' ? 'bg-yellow-50 border-yellow-300' :
            'bg-gray-50 border-gray-300'
          }`}>
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                threat.level === 'safe' ? 'bg-green-500' :
                threat.level === 'danger' ? 'bg-red-500' :
                threat.level === 'warning' ? 'bg-yellow-500' :
                'bg-gray-500'
              }`}>
                <span className="text-white text-xl">{threat.icon}</span>
              </div>
              <div>
                <h3 className={`text-lg font-bold ${
                  threat.level === 'safe' ? 'text-green-900' :
                  threat.level === 'danger' ? 'text-red-900' :
                  threat.level === 'warning' ? 'text-yellow-900' :
                  'text-gray-900'
                }`}>
                  {threat.label}
                </h3>
                <p className={`text-sm ${
                  threat.level === 'safe' ? 'text-green-700' :
                  threat.level === 'danger' ? 'text-red-700' :
                  threat.level === 'warning' ? 'text-yellow-700' :
                  'text-gray-700'
                }`}>
                  {threat.description}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Compact Summary Grid */}
        <div className="space-y-3">
          {Object.entries(summary).map(([key, value]) => {
            if (value === null || value === undefined) return null

            const formattedKey = key
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')

            return (
              <div key={key} className="bg-white rounded-lg border border-gray-200 p-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  {formattedKey}
                </h4>
                <div>{renderValue(value)}</div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderTechnical = () => {
    if (!analysis?.technical_report) return null

    return (
      <div className="space-y-4">
        <div className="bg-gray-900 p-6 rounded-xl border border-gray-700 shadow-2xl">
          <pre className="overflow-x-auto text-sm whitespace-pre-wrap font-mono text-green-400 leading-relaxed">
            {JSON.stringify(analysis.technical_report, null, 2)}
          </pre>
        </div>
      </div>
    )
  }

  const renderFullReport = () => {
    if (!analysis) return null

    return (
      <div className="space-y-4">
        <div className="bg-gray-900 p-6 rounded-xl border border-gray-700 shadow-2xl">
          <pre className="overflow-x-auto text-xs whitespace-pre-wrap font-mono text-green-400 leading-relaxed">
            {JSON.stringify(analysis, null, 2)}
          </pre>
        </div>
      </div>
    )
  }

  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      style={customStyles}
      contentLabel="Email Analysis"
    >
      {/* Compact Header */}
      <div className="bg-blue-600 px-4 py-3 flex-shrink-0">
        <div className="flex justify-between items-center gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-medium text-white truncate">
              {analysis?.email_headers?.subject || analysis?.subject || cleanFilename(email?.filename) || 'Email Analysis'}
            </h2>
          </div>
          <button
            onClick={close}
            className="flex-shrink-0 text-white hover:text-gray-200 p-1 transition-colors"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white px-4 flex-shrink-0">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('summary')}
            className={`py-3 px-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'summary'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setActiveTab('technical')}
            className={`py-3 px-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'technical'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Technical
          </button>
          <button
            onClick={() => setActiveTab('full')}
            className={`py-3 px-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'full'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Full Report
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
        {!analysis && (
          <div className="flex flex-col items-center justify-center py-20">
            <svg className="animate-spin h-16 w-16 text-blue-600 mb-6" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 font-bold text-lg">Analyzing email...</p>
            <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
          </div>
        )}

        {analysis && analysis.error && (
          <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-300 rounded-2xl p-8 shadow-xl">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-red-900 font-black text-2xl mb-2">Analysis Error</h3>
                <p className="text-red-700 text-base leading-relaxed">{analysis.error}</p>
              </div>
            </div>
          </div>
        )}

        {analysis && !analysis.error && (
          <div>
            {activeTab === 'summary' && renderSummary()}
            {activeTab === 'technical' && renderTechnical()}
            {activeTab === 'full' && renderFullReport()}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ModalView
